import { api } from '@/config/api';

export interface LeadGenFormSummary {
  id: string;
  name: string;
  status?: string;
  locale?: string;
  leads_count?: number;
  created_time?: string;
  page_id?: string;
}

export interface LeadGenFormDetail extends LeadGenFormSummary {
  questions?: Array<Record<string, unknown>>;
  follow_up_action_url?: string;
  form_type?: string;
  is_optimized_for_quality?: boolean;
}

export interface LeadRow {
  id: string;
  created_time?: string;
  ad_id?: string;
  ad_name?: string;
  adset_id?: string;
  campaign_id?: string;
  form_id?: string;
  field_data: Array<{ name: string; values: string[] }>;
}

export interface LeadGenFormQuestion {
  /** Standard Meta question type (FULL_NAME, EMAIL, PHONE, DOB, ...) or 'CUSTOM' */
  type: string;
  /** Required when type === 'CUSTOM' — the prompt shown to user */
  label?: string;
  /** Question key — Meta uses it to reference the question from `dependent_conditional_questions` */
  key?: string;
  /** Multiple-choice options. */
  options?: Array<{ value: string; key?: string }>;
  /** Show this question only when an earlier answer matches. */
  dependent_conditional_questions?: Array<{ key: string; value: string }>;
}

export interface CreateLeadFormPayload {
  name: string;
  privacyPolicyUrl: string;
  thankYouBody?: string;
  websiteUrl?: string;
  questions: LeadGenFormQuestion[];
  locale?: string;
  pageId?: string;
  formType?: 'MORE_VOLUME' | 'HIGHER_INTENT' | 'VERIFIED_PHONE_NUMBER' | 'CUSTOM';
  isOptimizedForQuality?: boolean;
  ctaType?: 'VIEW_WEBSITE' | 'CALL_BUSINESS' | 'DOWNLOAD' | 'NONE';
  ctaText?: string;
}

export interface LeadPageSubscription {
  pageId: string;
  subscribed: boolean;
  status: string;
  checkedAt: string;
  error?: string;
}

const BASE = '/slighter_crm/facebook/lead-forms';

/** Standard Meta question types per the live-tested valid enum (10 forms 8/10). */
export const STANDARD_LEAD_QUESTIONS: { type: string; label: string }[] = [
  { type: 'FULL_NAME', label: 'Full name' },
  { type: 'FIRST_NAME', label: 'First name' },
  { type: 'LAST_NAME', label: 'Last name' },
  { type: 'EMAIL', label: 'Email' },
  { type: 'PHONE', label: 'Phone number' },
  { type: 'WHATSAPP_NUMBER', label: 'WhatsApp number' },
  { type: 'COMPANY_NAME', label: 'Company name' },
  { type: 'JOB_TITLE', label: 'Job title' },
  { type: 'CITY', label: 'City' },
  { type: 'COUNTRY', label: 'Country' },
  { type: 'GENDER', label: 'Gender' },
  { type: 'DOB', label: 'Date of birth' },
  { type: 'MARITIAL_STATUS', label: 'Marital status' },
];

export const facebookLeadFormsService = {
  subscription: async (pageId: string, repair = false): Promise<LeadPageSubscription> =>
    (repair ? await api.post(`${BASE}/page-subscription`, {}, { params: { pageId } }) : await api.get(`${BASE}/page-subscription`, { params: { pageId } })).data,
  list: async (pageId?: string): Promise<LeadGenFormSummary[]> =>
    (await api.get(BASE, { params: { pageId } })).data,

  getById: async (formId: string): Promise<LeadGenFormDetail> =>
    (await api.get(`${BASE}/${formId}`)).data,

  create: async (payload: CreateLeadFormPayload): Promise<{ id: string; name: string; page_id: string }> =>
    (await api.post(BASE, payload)).data,

  /** Archive the form. Meta forbids hard-deletion of lead forms with leads. */
  remove: async (formId: string): Promise<{ success: boolean; archived: true }> =>
    (await api.delete(`${BASE}/${formId}`)).data,

  /** Fetch submitted leads. Use `?after` cursor for pagination. */
  fetchLeads: async (
    formId: string,
    opts: { limit?: number; after?: string; since?: string } = {},
  ): Promise<{ data: LeadRow[]; next?: string }> =>
    (await api.get(`${BASE}/${formId}/leads`, { params: opts })).data,
};
